import DuplicateModal from "@/components/core/modals/DuplicateModal";
import {
  requestDeleteModelTransaction,
  requestGetModelTransactionAttachments,
  requestGetModelTransactionFiles,
  requestGetModelTransactionSignatures,
  requestPostDuplicateModelTransaction,
  requestPostModelsTransactionsSearch,
} from "@/state/transactionModels/TransactionModelEffects";
import {
  resetTransaction,
  setCurrentTransactionField,
  setTransactionAttachments,
  setTransactionFiles,
  setTransactionIsFromDraft,
  setTransactionSignatories,
  setTransactionSignatures,
  setTransactionType,
} from "@/state/transactions/CurrentTransactionEvents";
import { useCurrentTransaction } from "@/state/transactions/CurrentTransactionHook";
import { TransactionType } from "@/type/core/transaction/transaction.types";
import { CONSEIL_EXPERT_COMPTABLES, DEFAULT_PAGE_SIZE } from "@/utils/Constant";
import { toastError } from "@/utils/helpers/toast-helper";
import { TransactionHelper } from "@/utils/helpers/transaction-helper";
import useOfficesToDisplay from "@/utils/hooks/useOfficesToDisplay";
import PageLayout from "@components/core/layouts/PageLayout";
import LinkButton from "@components/core/links/LinkButton";
import ConfirmModal from "@components/core/modals/ConfirmModal";
import InfoModal from "@components/core/modals/InfoModal";
import CardSection from "@components/core/section/CardSection";
import BasicTable from "@components/core/tables/BasicTable";
import BasicTabs from "@components/core/tabs/BasicTabs";
import { ROUTES } from "@routes/Routes";
import { FilterType, ModelTransaction } from "@type/core/models/models.types";
import { columns } from "@views/core/extranet/menu/offices/transactionModels/columns/TransactionModelsColumns";
import { Checkbox, TablePaginationConfig } from "antd";
import Search from "antd/es/input/Search";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface Filters {
  signatureCheck?: boolean;
  scellementCheck?: boolean;
}

const TransactionModelsScreen: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { transactionForm } = useCurrentTransaction();

  const {
    selectedOffice,
    setSelectedOffice,
    officesToDisplay,
    handleTabClick,
    activeKey,
  } = useOfficesToDisplay();

  const [search, setSearch] = useState<string>("");
  const [selectedFilters, setSelectedFilters] = useState<Filters>({
    signatureCheck: true,
    scellementCheck: true,
  });
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [isDeleteModelOpen, setIsDeleteModelOpen] = useState<boolean>(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] =
    useState<boolean>(false);
  const [models, setModels] = useState<ModelTransaction[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [transactionIdClicked, setTransactionIdClicked] = useState<string>();
  const [modelPreviousName, setModelPreviousName] = useState<string>();
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });

  const types: FilterType[] = [
    ...(selectedFilters.signatureCheck ? ["signature" as FilterType] : []),
    ...(selectedFilters.scellementCheck ? ["scellement" as FilterType] : []),
  ];

  useEffect(() => {
    // TODO see if there is a proper way to change selectedOffice when arriving from creating model screen
    if (transactionForm.office) {
      setSelectedOffice(
        officesToDisplay.find((office) => office.id === transactionForm.office),
      );
    }
  }, [transactionForm.office]);

  useEffect(() => {
    setIsLoading(true);
    void requestPostModelsTransactionsSearch({
      dto: {
        officeId: selectedOffice?.id,
        page: pagination.current,
        search,
        sort: { order: 0, type: "name" },
        types,
      },
    });
  }, [selectedFilters, selectedOffice, search, pagination.current]);

  useEffect(() => {
    requestPostModelsTransactionsSearch.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setModels(result.data.content);
        setPagination({
          current: result.data.current,
          total: result.data.max * DEFAULT_PAGE_SIZE,
        });
      } else {
        toastError(t("error.toast.model.transactions"));
      }
      setIsLoading(false);
    });
  }, []);

  const handleSearch = () => {
    void requestPostModelsTransactionsSearch({
      dto: {
        officeId: selectedOffice?.id,
        page: pagination.current,
        search,
        sort: { order: 0, type: "name" },
        types,
      },
    });
  };

  const handleEdit = (model: ModelTransaction) => {
    setIsLoading(true);

    const id = model.id;

    setTransactionType(model.transactionType);
    setTransactionIsFromDraft(true);
    setCurrentTransactionField([
      {
        field: "id",
        value: id,
      },
      {
        field: "reference",
        value: model.reference,
      },
      {
        field: "name",
        value: model.name,
      },
      {
        field: "office",
        value: model.officeId,
      },
      {
        field: "confidential",
        value: model.isConfidential,
      },
      {
        field: "invitationMode",
        value: model.invitationMode,
      },
    ]);
    setTransactionSignatories(model.signatories ?? []);

    Promise.all([
      requestGetModelTransactionAttachments({ id }),
      requestGetModelTransactionFiles({ id }),
      requestGetModelTransactionSignatures({ id }),
    ])
      .then((responses) => {
        const [attachmentsResponse, filesResponse, signaturesResponse] =
          responses;

        if (
          attachmentsResponse.ok &&
          filesResponse.ok &&
          signaturesResponse.ok
        ) {
          navigate(
            model.transactionType === "scellement"
              ? ROUTES.extranet.menu.offices.transactionModel.scellement.generate()
              : ROUTES.extranet.menu.offices.transactionModel.signature.generate(),
          );
          setTransactionAttachments(
            TransactionHelper.getAttachmentsFromModelAttachments(
              attachmentsResponse.data ?? [],
            ),
          );
          setTransactionFiles(
            TransactionHelper.getFilesFromModelDocuments(
              filesResponse.data ?? [],
            ),
          );
          setTransactionSignatures(
            signaturesResponse.data?.map((signature) => ({
              ...signature,
              email: signature ? signature.email : "scellement",
              name: signature ? signature.name : CONSEIL_EXPERT_COMPTABLES,
            })) ?? [],
          );
        } else {
          if (!attachmentsResponse.ok) {
            toastError(t("error.toast.model.attachments"));
          }
          if (!filesResponse.ok) {
            toastError(t("error.toast.model.files"));
          }
          if (!signaturesResponse.ok) {
            toastError(t("error.toast.model.signatures"));
          }
        }
      })
      .catch(() => {
        toastError(t("error.toast.model.transactionsAndAttachementsAndFiles"));
      })
      .finally(() => setIsLoading(false));
  };

  const handleDelete = () => {
    if (transactionIdClicked) {
      requestDeleteModelTransaction({ id: transactionIdClicked })
        .then((response) => {
          if (response.ok) {
            handleSearch();
          } else {
            toastError(t("error.toast.model.delete"));
          }
        })
        .catch(() => {
          toastError(t("error.toast.model.delete"));
        });
    }
  };

  const createModel = (transactionType: TransactionType) => {
    if (selectedOffice) {
      resetTransaction();
      setCurrentTransactionField([
        {
          field: "office",
          value: selectedOffice.id,
        },
      ]);
      setTransactionType(transactionType);
    }
  };

  const handleDuplicateModel = (name: string) => {
    if (transactionIdClicked && selectedOffice?.id) {
      setIsLoading(true);
      requestPostDuplicateModelTransaction({
        dto: { name, officeId: selectedOffice?.id },
        id: transactionIdClicked,
      })
        .then((response) => {
          if (response.ok) {
            setIsDuplicateModalOpen(false);
            void requestPostModelsTransactionsSearch({
              dto: {
                officeId: selectedOffice?.id,
                page: pagination.current,
                search,
                sort: { order: 0, type: "name" },
                types,
              },
            });
          } else {
            toastError(t("error.toast.model.transactionsDuplicate"));
            setIsLoading(false);
          }
        })
        .catch(() => {
          toastError(t("error.toast.model.transactionsDuplicate"));
          setIsLoading(false);
        });
    }
  };

  const handleCurrentPageChange = (pagination: TablePaginationConfig) =>
    setPagination({
      ...pagination,
      current: pagination.current,
    });

  return (
    <PageLayout container returnRoute={ROUTES.extranet.home.generate()}>
      <>
        <CardSection
          title={
            <>
              {t("offices.transactionModel.title")}
              <InfoModal value="transactionModel" info />
            </>
          }
        >
          <BasicTabs
            onTabClick={handleTabClick}
            activeKey={activeKey}
            tabs={officesToDisplay.map((office) => {
              return {
                title: office.alias,
                content: (
                  <>
                    <div className="d-flex flex-column flex-md-row align-items-center justify-content-center gap-24 mb-24">
                      <LinkButton
                        to={ROUTES.extranet.menu.offices.transactionModel.signature.generate()}
                        onClick={() => createModel("signature")}
                        text={t("offices.transactionModel.addSignatureModel")}
                        dataAlt={t(
                          "offices.transactionModel.addSignatureModel",
                        )}
                        className="mw-340"
                      />
                      <span className="text-black font-large px-8">
                        {t("common.or")}
                      </span>
                      <LinkButton
                        to={ROUTES.extranet.menu.offices.transactionModel.scellement.generate()}
                        onClick={() => createModel("scellement")}
                        text={t("offices.transactionModel.addSealModel")}
                        dataAlt={t("offices.transactionModel.addSealModel")}
                        className="mw-340"
                      />
                    </div>
                    <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-md-center gap-8 mb-24">
                      <span>{t("offices.transactionModel.searchModels")}</span>
                      <Search
                        id="filterSearch"
                        placeholder={t(
                          "offices.transactionModel.researchModel",
                        )}
                        value={search}
                        onChange={(e) => {
                          e.preventDefault();
                          setSearch(e.target.value);
                        }}
                        className="search ml-md-8 w-md-50"
                        onPressEnter={handleSearch}
                        onSearch={handleSearch}
                        enterButton
                      />
                    </div>
                    <div className="d-flex align-items-center flex-wrap">
                      <Checkbox
                        id="signatureCheck"
                        checked={selectedFilters.signatureCheck}
                        onChange={() => {
                          !selectedFilters.scellementCheck &&
                          selectedFilters.signatureCheck
                            ? setIsConfirmModalOpen(true)
                            : setSelectedFilters({
                                ...selectedFilters,
                                signatureCheck: !selectedFilters.signatureCheck,
                              });
                        }}
                        className="mr-32 mb-24"
                      >
                        {t("signature.label")}
                      </Checkbox>
                      <Checkbox
                        id="scellementCheck"
                        checked={selectedFilters.scellementCheck}
                        onChange={() => {
                          !selectedFilters.signatureCheck &&
                          selectedFilters.scellementCheck
                            ? setIsConfirmModalOpen(true)
                            : setSelectedFilters({
                                ...selectedFilters,
                                scellementCheck:
                                  !selectedFilters.scellementCheck,
                              });
                        }}
                        className="mb-24"
                      >
                        {t("scellement.label")}
                      </Checkbox>
                    </div>
                    <div className="min-height-table">
                      <BasicTable
                        isLoading={isLoading}
                        columns={columns(
                          handleEdit,
                          (transactionIdClicked: string) => {
                            setTransactionIdClicked(transactionIdClicked);
                            setIsDeleteModelOpen(true);
                          },
                          (
                            transactionIdClicked: string,
                            modelPreviousName: string,
                          ) => {
                            setTransactionIdClicked(transactionIdClicked);
                            setModelPreviousName(modelPreviousName);
                            setIsDuplicateModalOpen(true);
                          },
                        )}
                        dataSource={models}
                        pagination={pagination}
                        onChange={handleCurrentPageChange}
                      />
                    </div>
                  </>
                ),
              };
            })}
          />
        </CardSection>
        <ConfirmModal
          visible={isDeleteModelOpen}
          onCancel={() => setIsDeleteModelOpen(false)}
          onOk={() => {
            handleDelete();
            setIsDeleteModelOpen(false);
          }}
          title={t("offices.transactionModel.deleteModel")}
        />
        <ConfirmModal
          visible={isConfirmModalOpen}
          onCancel={() => setIsConfirmModalOpen(false)}
          onOk={() => {
            setIsConfirmModalOpen(false);
            selectedFilters.signatureCheck
              ? setSelectedFilters({
                  ...selectedFilters,
                  signatureCheck: !selectedFilters.signatureCheck,
                })
              : setSelectedFilters({
                  ...selectedFilters,
                  scellementCheck: !selectedFilters.scellementCheck,
                });
          }}
          title={t("offices.transactionModel.uncheckSignatureTypesModal.title")}
          content={t(
            "offices.transactionModel.uncheckSignatureTypesModal.body",
          )}
        />
        {transactionIdClicked && modelPreviousName && (
          <DuplicateModal
            visible={isDuplicateModalOpen}
            onCancel={() => setIsDuplicateModalOpen(false)}
            onSubmit={handleDuplicateModel}
            previousName={modelPreviousName}
          />
        )}
      </>
    </PageLayout>
  );
};

export default TransactionModelsScreen;
